import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Clock, Phone, Mail, Check, Ban, CalendarPlus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SectionHeader } from "@/components/ui-kit";
import { qrUrl } from "@/components/ShareBar";

export default function DropOff() {
  useSeo("Donation Drop-Off", "Drop off in-kind donations to Golden Wolf Foundation. Address, receiving hours, map, parking and an appointment scheduler.");
  const [d, setD] = useState(null);
  const [appt, setAppt] = useState({ name: "", email: "", phone: "", available_datetime: "", message: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/dropoff").then((r) => setD(r.data)); }, []);
  if (!d) return null;

  const schedule = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/contact", { ...appt, subject: "Drop-off appointment request", type: "dropoff-appointment" }); toast.success("Appointment request sent — we'll confirm shortly."); setAppt({ name: "", email: "", phone: "", available_datetime: "", message: "" }); }
    catch { toast.error("Could not send request"); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <SectionHeader overline="Bring It By" title="Donation Drop-Off" sub="Dropping off an item in person? Here's everything you need." />
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="gw-card p-6 space-y-4">
            <Row icon={MapPin} label="Address" value={d.address} />
            <Row icon={Clock} label="Receiving hours" value={d.hours} />
            <Row icon={Phone} label="Phone" value={d.contact_phone} />
            <Row icon={Mail} label="Email" value={d.contact_email} />
            <p className="text-sm text-[#C8C4B7] pt-2 border-t border-white/10">{d.parking}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="gw-card p-5"><div className="overline mb-3 text-[#4A5D23]">Accepted</div><ul className="space-y-1.5">{d.accepted.map((a) => <li key={a} className="flex gap-2 text-sm text-[#C8C4B7]"><Check className="w-4 h-4 text-[#4A5D23] shrink-0 mt-0.5" />{a}</li>)}</ul></div>
            <div className="gw-card p-5"><div className="overline mb-3 text-red-400">Prohibited</div><ul className="space-y-1.5">{d.prohibited.map((a) => <li key={a} className="flex gap-2 text-sm text-[#C8C4B7]"><Ban className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />{a}</li>)}</ul></div>
          </div>
          <div className="gw-card p-6 flex items-center gap-5">
            <img src={qrUrl(`${window.location.origin}/wishlist`, 140)} alt="QR code to donation form" className="w-28 h-28 bg-[#0A0A0A] p-1 border border-white/10" />
            <div><h4 className="font-heading text-lg text-[#FDFBF7]">Scan to pledge an item</h4><p className="text-sm text-[#8A877B] mt-1">This QR code opens the in-kind wish list so you can log your donation before you arrive.</p></div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="gw-card overflow-hidden h-72">
            <iframe title="Drop-off location map" className="w-full h-full grayscale contrast-125" loading="lazy"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(d.map_query || d.address)}&output=embed`} />
          </div>
          <form onSubmit={schedule} className="gw-card p-6 space-y-4">
            <div className="flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-xl text-[#FDFBF7]">Schedule an Appointment</h3></div>
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="Name *" className="gw-input" data-testid="appt-name" value={appt.name} onChange={(e) => setAppt({ ...appt, name: e.target.value })} />
              <input type="email" required placeholder="Email *" className="gw-input" data-testid="appt-email" value={appt.email} onChange={(e) => setAppt({ ...appt, email: e.target.value })} />
            </div>
            <input placeholder="Phone" className="gw-input" data-testid="appt-phone" value={appt.phone} onChange={(e) => setAppt({ ...appt, phone: e.target.value })} />
            <input type="datetime-local" className="gw-input" data-testid="appt-datetime" value={appt.available_datetime} onChange={(e) => setAppt({ ...appt, available_datetime: e.target.value })} />
            <textarea rows={2} placeholder="What are you dropping off?" className="gw-input" data-testid="appt-message" value={appt.message} onChange={(e) => setAppt({ ...appt, message: e.target.value })} />
            <button disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2 py-3" data-testid="appt-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Request Appointment</button>
          </form>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3"><Icon className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" /><div><div className="text-xs text-[#8A877B] uppercase tracking-wider">{label}</div><div className="text-[#FDFBF7]">{value}</div></div></div>
);
